import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Container,
  Spinner,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  FormGroup,
  Label,
  Input,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DataTable, { TableColumn, ConditionalStyles } from "react-data-table-component";
import { requestJson } from "../../../helpers/api";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import baconLogo from "../../../assets/images/baconappen.png";
import {
  buildVelzonDataTableStyles,
  ensureVelzonDataTableThemes,
} from "../../../Components/Common/dataTableTheme";

ensureVelzonDataTableThemes();

const ensureText = (valor: unknown) => {
  if (typeof valor === "string") {
    return valor;
  }
  if (valor == null) {
    return "";
  }
  return String(valor);
};

const normalizarTexto = (valor: unknown) => String(valor ?? "").trim().toLowerCase();

const claveCuentaServidor = (codigo: unknown, servidor: unknown) => {
  const codigoNormalizado = normalizarTexto(codigo);
  if (!codigoNormalizado) {
    return "";
  }
  return `${normalizarTexto(servidor)}::${codigoNormalizado}`;
};

const obtenerNivelIndent = (valor: unknown) => {
  const nivel = Number(valor);
  if (!Number.isFinite(nivel)) {
    return 0;
  }
  return Math.max(0, nivel - 1);
};

const LEVEL_INDENT_PX = 16;

const PLAN_CUENTA_COLUMNS = [
  { key: "idplancuenta", header: "Cuenta" },
  { key: "idnivel", header: "Nivel" },
  { key: "idpadre", header: "Cuenta padre" },
  { key: "tipo_cuenta_descripcion", header: "Tipo de cuenta" },
] as const;

const SERVER_DISPLAY_NAMES: Record<string, string> = {
  bcn_server: "Paradise S.A.",
  gama_server: "Gama S.A.",
  parla_server: "Parla S.A.",
};

const humanizarColumnaArticulos = (clave: string) =>
  clave
    .split("_")
    .filter((segmento) => segmento.length > 0)
    .map((segmento) => segmento.charAt(0).toUpperCase() + segmento.slice(1))
    .join(" ");

const SERVER_ORDER = ["parla_server", "gama_server", "bcn_server"] as const;
const SERVER_PRIORITY: Record<string, number> = SERVER_ORDER.reduce(
  (acumulado, valor, index) => {
    acumulado[valor] = index;
    return acumulado;
  },
  {} as Record<string, number>,
);

type CuentaSeleccionada = {
  codigo: string;
  nombre: string;
  servidor: string;
};

type FullscreenOffcanvasStyle = CSSProperties & {
  "--bs-offcanvas-width"?: string;
};

const FULLSCREEN_OFFCANVAS_STYLE: FullscreenOffcanvasStyle = {
  "--bs-offcanvas-width": "100%",
  width: "100vw",
  maxWidth: "100vw",
  height: "100vh",
  maxHeight: "100vh",
};

const formatServerLabel = (valor: string) => {
  if (!valor) {
    return "Todos los servidores";
  }
  const clave = valor.trim().toLowerCase();
  const etiqueta = SERVER_DISPLAY_NAMES[clave];
  if (etiqueta) {
    return etiqueta;
  }
  const palabras = valor
    .replace(/_/g, " ")
    .split(" ")
    .filter((palabra) => palabra.length > 0);
  if (palabras.length === 0) {
    return valor;
  }
  return palabras
    .map((palabra) => palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase())
    .join(" ");
};

const resolverUsuarioActual = () => {
  if (typeof window === "undefined") {
    return "Usuario no identificado";
  }
  const raw =
    sessionStorage.getItem("authUser") ||
    localStorage.getItem("authUser") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("user");
  if (!raw) {
    return "Usuario no identificado";
  }
  try {
    const parsed = JSON.parse(raw);
    const nested =
      parsed?.usuario ||
      parsed?.user ||
      parsed?.datosUsuario ||
      parsed?.data?.usuario ||
      parsed?.data?.user;
    const candidatos = [
      parsed?.name,
      parsed?.fullName,
      parsed?.username,
      parsed?.email,
      nested?.nombre,
      nested?.nombreUsuario,
      nested?.username,
      nested?.email,
    ];
    const texto = candidatos.find((valor) => typeof valor === "string" && valor.trim().length > 0);
    if (texto) {
      return texto;
    }
  } catch {
    // ignorado
  }
  return "Usuario no identificado";
};

const ContabilidadPlanCuentasPage = () => {
  const [datos, setDatos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [filtrosVisibles, setFiltrosVisibles] = useState(false);
  const [servidorActivo, setServidorActivo] = useState("");
  const [filtroNivel, setFiltroNivel] = useState("");
  const [filtroTipoCuenta, setFiltroTipoCuenta] = useState("");
  const [filtroSaldo, setFiltroSaldo] = useState("");
  const [filtroTexto, setFiltroTexto] = useState("");
  const [soloSinPadre, setSoloSinPadre] = useState(false);
  const [soloConAsiento, setSoloConAsiento] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<"light" | "dark">(
    typeof document !== "undefined" && document.body.getAttribute("data-bs-theme") === "dark"
      ? "dark"
      : "light",
  );
  const [articulosVisibles, setArticulosVisibles] = useState(false);
  const [articulosCuenta, setArticulosCuenta] = useState<Record<string, unknown>[]>([]);
  const [articulosCargando, setArticulosCargando] = useState(false);
  const [articulosError, setArticulosError] = useState<string | null>(null);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState<CuentaSeleccionada | null>(null);
  const [articulosCatalogo, setArticulosCatalogo] = useState<Record<string, unknown>[]>([]);
  const [articulosCatalogoCargando, setArticulosCatalogoCargando] = useState(false);
  const [articulosCatalogoError, setArticulosCatalogoError] = useState<string | null>(null);
  const [exportandoArticulosExcel, setExportandoArticulosExcel] = useState(false);
  const [exportandoArticulosPdf, setExportandoArticulosPdf] = useState(false);
  const [articulosExportError, setArticulosExportError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Contabilidad - Plan de cuentas | Saas";
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const respuesta = await requestJson<any[]>("/api/informes/contabilidad/plan-cuentas");
      setDatos(Array.isArray(respuesta) ? respuesta : []);
    } catch (err) {
      const mensaje = err instanceof Error ? err.message : "No pudimos obtener la información.";
      setError(mensaje);
      setDatos([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    let activo = true;
    const cargarLogo = async () => {
      const fuentes = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
      for (const fuente of fuentes) {
        if (!fuente) continue;
        try {
          const respuesta = await fetch(fuente);
          if (!respuesta.ok) continue;
          const blob = await respuesta.blob();
          const lector = new FileReader();
          const resultado = await new Promise<string>((resolve, reject) => {
            lector.onloadend = () => {
              if (typeof lector.result === "string") {
                resolve(lector.result);
              } else {
                reject(new Error("No pudimos leer el logo."));
              }
            };
            lector.onerror = () => reject(new Error("No pudimos leer el logo."));
            lector.readAsDataURL(blob);
          });
          if (activo) {
            setLogoDataUrl(resultado);
          }
          return;
        } catch {
          continue;
        }
      }
    };
    void cargarLogo();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const target = document.body;
    const updateMode = () => {
      const modo = target.getAttribute("data-bs-theme") === "dark" ? "dark" : "light";
      setThemeMode(modo);
    };
    updateMode();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.attributeName === "data-bs-theme")) {
        updateMode();
      }
    });
    observer.observe(target, { attributes: true, attributeFilter: ["data-bs-theme"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let activo = true;
    const cargarArticulosCatalogo = async () => {
      setArticulosCatalogoCargando(true);
      setArticulosCatalogoError(null);
      try {
        const respuesta = await requestJson<any[]>("/api/informes/contabilidad/articulos-cuentas");
        if (!activo) {
          return;
        }
        const registros = (Array.isArray(respuesta) ? respuesta : []) as Record<string, unknown>[];
        setArticulosCatalogo(registros);
      } catch (err) {
        if (!activo) {
          return;
        }
        const mensaje = err instanceof Error ? err.message : "No pudimos obtener los artículos.";
        setArticulosCatalogo([]);
        setArticulosCatalogoError(mensaje);
      } finally {
        if (activo) {
          setArticulosCatalogoCargando(false);
        }
      }
    };
    void cargarArticulosCatalogo();
    return () => {
      activo = false;
    };
  }, []);

  const manejarVerArticulos = useCallback(
    (fila: Record<string, unknown>) => {
      const codigo = ensureText(fila.idplancuenta ?? "");
      const nombre = ensureText(fila.nombre ?? "");
      const servidor = ensureText(fila.server ?? "");
      if (!codigo) {
        setArticulosError("No pudimos identificar la cuenta seleccionada.");
        setArticulosVisibles(true);
        setArticulosCuenta([]);
        return;
      }
      setCuentaSeleccionada({ codigo, nombre, servidor });
      setArticulosVisibles(true);
      setArticulosError(null);
      setArticulosExportError(null);
      setArticulosCuenta([]);
      if (articulosCatalogoCargando) {
        setArticulosError("Aún estamos cargando la lista de artículos. Intentá nuevamente.");
        return;
      }
      if (articulosCatalogoError) {
        setArticulosError(articulosCatalogoError);
        return;
      }
      setArticulosCargando(true);
      try {
        const registros = articulosCatalogo;
        const cuentaNormalizada = normalizarTexto(codigo);
        const servidorNormalizado = normalizarTexto(servidor);
        const coincidencias = registros.filter((registro) => {
          if (servidorNormalizado && normalizarTexto(registro?.server) !== servidorNormalizado) {
            return false;
          }
          const camposCuentas = [
            registro?.idplancuenta_c,
            registro?.idplancuenta_vgr,
            registro?.idplancuenta_vex,
          ];
          return camposCuentas.some((valor) => normalizarTexto(valor) === cuentaNormalizada);
        });
        setArticulosCuenta(coincidencias);
      } catch (err) {
        const mensaje = err instanceof Error ? err.message : "No pudimos obtener los artículos.";
        setArticulosError(mensaje);
        setArticulosCuenta([]);
      } finally {
        setArticulosCargando(false);
      }
    },
    [articulosCatalogo, articulosCatalogoCargando, articulosCatalogoError],
  );

  const cerrarArticulos = useCallback(() => {
    setArticulosVisibles(false);
    setArticulosExportError(null);
  }, []);

  const cuentasConArticulos = useMemo(() => {
    const conjunto = new Set<string>();
    articulosCatalogo.forEach((fila) => {
      if (!fila) {
        return;
      }
      ["idplancuenta_c", "idplancuenta_vgr", "idplancuenta_vex"].forEach((campo) => {
        const clave = claveCuentaServidor(
          (fila as Record<string, unknown>)[campo],
          (fila as Record<string, unknown>).server,
        );
        if (clave) {
          conjunto.add(clave);
        }
      });
    });
    return conjunto;
  }, [articulosCatalogo]);

  const dataTableColumns = useMemo<TableColumn<Record<string, unknown>>[]>(() => {
    const columnas: TableColumn<Record<string, unknown>>[] = [
      {
        name: "Cuenta",
        selector: (row) => ensureText(row.idplancuenta ?? ""),
        sortable: false,
        grow: 3,
        style: { minWidth: "320px" },
        cell: (row) => {
          const codigo = ensureText(row.idplancuenta ?? "");
          const nombre = ensureText(row.nombre ?? "");
          const indent = obtenerNivelIndent(row.idnivel) * LEVEL_INDENT_PX;
          return (
            <span
              className="d-inline-flex flex-column"
              style={indent > 0 ? { paddingLeft: `${indent}px` } : undefined}
            >
              <span className="fw-semibold">{codigo || "—"}</span>
              {nombre ? <small className="text-muted">{nombre}</small> : null}
            </span>
          );
        },
      },
      {
        name: "Nivel",
        selector: (row) => ensureText(row.idnivel ?? ""),
        sortable: false,
        width: "120px",
      },
      {
        name: "Cuenta padre",
        selector: (row) => ensureText(row.idpadre ?? ""),
        sortable: false,
        grow: 1,
        cell: (row) => {
          const codigo = ensureText(row.idpadre ?? "");
          const nombre = ensureText(row.padre_nombre ?? "");
          if (!codigo && !nombre) {
            return <span>—</span>;
          }
          return (
            <span className="d-inline-flex flex-column">
              <span>{codigo || "—"}</span>
              {nombre ? <small className="text-muted">{nombre}</small> : null}
            </span>
          );
        },
      },
      {
        name: "Tipo de cuenta",
        selector: (row) => ensureText(row.tipo_cuenta_descripcion ?? ""),
        sortable: false,
        grow: 1.2,
      },
    ];
    columnas.push({
      name: "Acciones",
      button: true,
      cell: (row) => {
        const clave = claveCuentaServidor(row.idplancuenta ?? "", row.server ?? "");
        const tieneArticulos =
          !articulosCatalogoCargando && !!clave && cuentasConArticulos.has(clave);
        const manejarClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
          event.preventDefault();
          if (!tieneArticulos) {
            return;
          }
          void manejarVerArticulos(row);
        };
        return (
          <a
            href="#!"
            role="button"
            className={`btn btn-sm ${
              tieneArticulos ? "btn-link text-secondary" : "btn-link text-muted disabled"
            } p-0`}
            onClick={manejarClick}
            aria-disabled={!tieneArticulos}
          >
            <i className="ri-eye-line me-1" />
            Ver artículos
          </a>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      grow: 0,
      width: "180px",
    });
    return columnas;
  }, [articulosCatalogoCargando, cuentasConArticulos, manejarVerArticulos]);

  const nivelesDisponibles = useMemo(() => {
    const conjunto = new Set<string>();
    datos.forEach((fila) => {
      const valor = fila.idnivel != null ? String(fila.idnivel) : "";
      if (valor) {
        conjunto.add(valor);
      }
    });
    return Array.from(conjunto).sort((a, b) => Number(a) - Number(b));
  }, [datos]);

  const saldosDisponibles = useMemo(() => {
    const conjunto = new Set<string>();
    datos.forEach((fila) => {
      const valor = String(fila.tipo_saldo ?? "").trim();
      if (valor) {
        conjunto.add(valor);
      }
    });
    return Array.from(conjunto).sort((a, b) => a.localeCompare(b));
  }, [datos]);

  const servidoresDisponibles = useMemo(() => {
    const conjunto = new Set<string>();
    datos.forEach((fila) => {
      const valor = String(fila.server ?? "").trim();
      if (valor) {
        conjunto.add(valor);
      }
    });
    const normalizarServidor = (valor: string) => valor.trim().toLowerCase();
    return Array.from(conjunto).sort((a, b) => {
      const prioridadA = SERVER_PRIORITY[normalizarServidor(a)];
      const prioridadB = SERVER_PRIORITY[normalizarServidor(b)];
      if (prioridadA !== undefined && prioridadB !== undefined) {
        return prioridadA - prioridadB;
      }
      if (prioridadA !== undefined) {
        return -1;
      }
      if (prioridadB !== undefined) {
        return 1;
      }
      return a.localeCompare(b);
    });
  }, [datos]);

  const servidoresTabs = useMemo(() => [...servidoresDisponibles], [servidoresDisponibles]);

  useEffect(() => {
    if (servidoresDisponibles.length === 0) {
      if (servidorActivo) {
        setServidorActivo("");
      }
      return;
    }
    const existe = servidorActivo
      ? servidoresDisponibles.some(
          (servidor) => servidor.toLowerCase() === servidorActivo.toLowerCase(),
        )
      : false;
    if (!servidorActivo || !existe) {
      const siguiente = servidoresDisponibles[0];
      if (siguiente && siguiente !== servidorActivo) {
        setServidorActivo(siguiente);
      }
    }
  }, [servidoresDisponibles, servidorActivo]);

  const registrosFiltrados = useMemo(() => {
    const normalizar = (valor: unknown) => String(valor ?? "").trim().toLowerCase();
    return datos.filter((fila) => {
      if (servidorActivo && normalizar(fila.server) !== servidorActivo.toLowerCase()) {
        return false;
      }
      if (filtroNivel && normalizar(fila.idnivel) !== filtroNivel.toLowerCase()) {
        return false;
      }
      if (filtroSaldo && normalizar(fila.tipo_saldo) !== filtroSaldo.toLowerCase()) {
        return false;
      }
      if (
        filtroTipoCuenta &&
        !(
          normalizar(fila.tipo_cuenta_descripcion).includes(filtroTipoCuenta.toLowerCase()) ||
          normalizar(fila.tipo_cuenta_codigo).includes(filtroTipoCuenta.toLowerCase())
        )
      ) {
        return false;
      }
      if (
        filtroTexto &&
        !(
          normalizar(fila.idplancuenta).includes(filtroTexto.toLowerCase()) ||
          normalizar(fila.nombre).includes(filtroTexto.toLowerCase())
        )
      ) {
        return false;
      }
      if (soloSinPadre && normalizar(fila.idpadre) !== "") {
        return false;
      }
      if (soloConAsiento && !(Number(fila.asiento ?? 0) > 0)) {
        return false;
      }
      return true;
    });
  }, [
    datos,
    servidorActivo,
    filtroNivel,
    filtroSaldo,
    filtroTipoCuenta,
    filtroTexto,
    soloSinPadre,
    soloConAsiento,
  ]);

  const limpiarFiltros = () => {
    setServidorActivo(servidoresDisponibles[0] ?? "");
    setFiltroNivel("");
    setFiltroSaldo("");
    setFiltroTipoCuenta("");
    setFiltroTexto("");
    setSoloSinPadre(false);
    setSoloConAsiento(false);
  };

  const manejarExportarExcel = useCallback(() => {
    if (registrosFiltrados.length === 0) {
      return;
    }
    setExportError(null);
    setExportandoExcel(true);
    try {
      const worksheet = XLSX.utils.json_to_sheet(registrosFiltrados);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "plan_cuentas");
      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `plan-cuentas-${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos generar la planilla de Excel. Intentá nuevamente.";
      setExportError(mensaje);
    } finally {
      setExportandoExcel(false);
    }
  }, [registrosFiltrados]);

  const manejarExportarPdf = useCallback(() => {
    if (registrosFiltrados.length === 0) {
      return;
    }
    setExportError(null);
    setExportandoPdf(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let pageWidth = doc.internal.pageSize.getWidth();
      let pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 28;
      const marginY = 28;
      const lineHeight = 12;
      const headerHeight = 110;
      const columnWeights = PLAN_CUENTA_COLUMNS.map((col) => {
        if (col.key === "idplancuenta") {
          return 3;
        }
        if (col.key === "idpadre") {
          return 1.5;
        }
        return 1;
      });
      const weightsTotal = columnWeights.reduce((acc, value) => acc + value, 0);
      const columnWidths = PLAN_CUENTA_COLUMNS.map((_, index) => {
        const baseWidth = ((pageWidth - marginX * 2) * columnWeights[index]) / weightsTotal;
        const key = PLAN_CUENTA_COLUMNS[index].key;
        let minWidth = 60;
        if (key === "idplancuenta") {
          minWidth = 360;
        } else if (key === "idpadre") {
          minWidth = 220;
        }
        return Math.max(baseWidth, minWidth);
      });
      const usuarioTexto = ensureText(resolverUsuarioActual());
      const timestamp = new Date();
      const timestampTexto = timestamp.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "medium" });
      const breadcrumbTexto = "Informes / Contabilidad / Plan de cuentas";
      const tituloTexto = "Plan de cuentas";
      const descripcionTexto =
        "Catálogo contable integrado desde Firebird. Filtrá por nivel, tipo de cuenta o saldo para validar configuración.";
      let currentY = marginY;
      let pageNumber = 1;

      const renderFooter = () => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const footerText = `Generado por ${usuarioTexto} · ${timestampTexto} · Página ${pageNumber}`;
        doc.text(footerText, marginX, pageHeight - marginY / 2);
      };

      const renderHeader = (isFirstPage: boolean) => {
        if (!isFirstPage) {
          renderFooter();
          doc.addPage();
          pageNumber += 1;
          pageWidth = doc.internal.pageSize.getWidth();
          pageHeight = doc.internal.pageSize.getHeight();
        }
        currentY = marginY;
        if (logoDataUrl) {
          doc.addImage(logoDataUrl, "PNG", marginX, currentY - 4, 120, 26);
          currentY += 26;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(breadcrumbTexto, marginX, currentY + 6);
        currentY += 16;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text(tituloTexto, marginX, currentY + 6);
        currentY += 20;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(descripcionTexto, marginX, currentY + 4, {
          maxWidth: pageWidth - marginX * 2 - 150,
        });
        doc.setFont("helvetica", "bold");
        doc.text("Usuario:", pageWidth - marginX, marginY + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(usuarioTexto || "—", pageWidth - marginX, marginY + 26, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Generado:", pageWidth - marginX, marginY + 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(timestampTexto || "—", pageWidth - marginX, marginY + 50, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`Página ${pageNumber}`, pageWidth - marginX, marginY + 62, { align: "right" });
        currentY = marginY + headerHeight;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        let currentX = marginX;
        PLAN_CUENTA_COLUMNS.forEach((column, index) => {
          const colWidth = columnWidths[index];
          const headerX = currentX;
          const lines = doc.splitTextToSize(column.header, colWidth - 6);
          lines.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, headerX, currentY + lineaIdx * lineHeight);
          });
          currentX += colWidth;
        });
        currentY += lineHeight * 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
      };

      const renderRow = (row: Record<string, unknown>) => {
        const values = PLAN_CUENTA_COLUMNS.map((column) => {
          const valor = row[column.key];
          if (valor === null || valor === undefined) {
            return "";
          }
          if (typeof valor === "number") {
            return Intl.NumberFormat("es-PY").format(valor);
          }
          if (column.key === "idplancuenta") {
            const codigo = ensureText(row.idplancuenta ?? "");
            const nombre = ensureText(row.nombre ?? "");
            const indentLevel = obtenerNivelIndent(row.idnivel);
            const indent =
              indentLevel > 0 ? " ".repeat(Math.min(indentLevel * 3, 30)) : "";
            const contenido = nombre ? `${codigo} - ${nombre}` : codigo;
            return `${indent}${contenido}`;
          }
          if (column.key === "idpadre") {
            const codigoPadre = ensureText(row.idpadre ?? "");
            const nombrePadre = ensureText(row.padre_nombre ?? "");
            return nombrePadre ? `${codigoPadre} - ${nombrePadre}` : codigoPadre;
          }
          return String(valor);
        });
        const rowLines = values.map((texto, index) =>
          doc.splitTextToSize(texto || "-", columnWidths[index] - 6),
        );
        const maxLines = Math.max(...rowLines.map((lineas) => (lineas.length > 0 ? lineas.length : 1)));
        const requiredHeight = maxLines * lineHeight + 6;
        if (currentY + requiredHeight > pageHeight - marginY) {
          renderHeader(false);
        }
        let currentX = marginX;
        rowLines.forEach((lineas, index) => {
          const baseX = currentX;
          lineas.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, baseX, currentY + lineaIdx * lineHeight);
          });
          currentX += columnWidths[index];
        });
        currentY += requiredHeight;
      };

      renderHeader(true);
      registrosFiltrados.forEach((fila) => renderRow(fila));
      renderFooter();
      const timestampArchivo = timestamp.toISOString().slice(0, 10);
      doc.save(`plan-cuentas-${timestampArchivo}.pdf`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos generar el PDF. Intentá nuevamente.";
      setExportError(mensaje);
    } finally {
      setExportandoPdf(false);
    }
  }, [registrosFiltrados, logoDataUrl]);

  const tableRows = useMemo(
    () =>
      registrosFiltrados.map((fila, index) => ({
        _rowId: `${fila.server ?? "srv"}-${fila.idplancuenta ?? index}-${index}`,
        ...fila,
      })),
    [registrosFiltrados],
  );

  const dataTableStyles = useMemo(() => buildVelzonDataTableStyles(themeMode), [themeMode]);

  const tableWrapperStyle = useMemo(
    () => ({
      backgroundColor: themeMode === "dark" ? "rgba(15, 23, 42, 0.9)" : "#fff",
      borderRadius: "0.75rem",
      border: `1px solid ${themeMode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      overflow: "hidden",
    }),
    [themeMode],
  );

  const conditionalRowStyles = useMemo(
    () =>
      [
        {
          when: (_row: Record<string, unknown>, index: number) => index % 2 === 0,
          style: {
            backgroundColor: themeMode === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.01)",
          },
        },
      ] as ConditionalStyles<Record<string, unknown>>[],
    [themeMode],
  );

  const articulosColumnas = useMemo(() => {
    if (articulosCuenta.length === 0) {
      return [] as string[];
    }
    const claves: string[] = [];
    const vistos = new Set<string>();
    articulosCuenta.forEach((fila) => {
      if (!fila) {
        return;
      }
      Object.keys(fila).forEach((clave) => {
        if (clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")) {
          return;
        }
        if (!vistos.has(clave)) {
          vistos.add(clave);
          claves.push(clave);
        }
      });
    });
    return claves;
  }, [articulosCuenta]);

  const articulosNombrePorColumna = useMemo(() => {
    if (articulosCuenta.length === 0) {
      return {} as Record<string, string>;
    }
    const mapa: Record<string, string> = {};
    articulosCuenta.forEach((fila) => {
      if (!fila) {
        return;
      }
      Object.keys(fila).forEach((clave) => {
        if (clave.startsWith("plan_cuenta") && clave.endsWith("_nombre")) {
          const middle = clave.slice("plan_cuenta".length, -"_nombre".length);
          const base = `idplancuenta${middle}`;
          mapa[base] = clave;
        }
      });
    });
    return mapa;
  }, [articulosCuenta]);

  const articulosDataTableColumns = useMemo<TableColumn<Record<string, unknown>>[]>(() => {
    return articulosColumnas.map((clave) => ({
      name: humanizarColumnaArticulos(clave),
      selector: (row) => ensureText(row[clave] ?? ""),
      grow: 1,
      wrap: true,
      cell: (row) => {
        const valor = ensureText(row[clave] ?? "");
        const nombreCampo = articulosNombrePorColumna[clave];
        if (nombreCampo) {
          const descripcion = ensureText(row[nombreCampo] ?? "");
          return (
            <span className="d-inline-flex flex-column">
              <span className="fw-semibold">{valor || "—"}</span>
              {descripcion ? <small className="text-muted">{descripcion}</small> : null}
            </span>
          );
        }
        return valor || "—";
      },
    }));
  }, [articulosColumnas, articulosNombrePorColumna]);

  const articulosTableRows = useMemo(
    () =>
      articulosCuenta.map((fila, index) => ({
        _rowId: `art-${index}`,
        ...fila,
      })),
    [articulosCuenta],
  );

  const construirTextoArticulo = useCallback(
    (fila: Record<string, unknown>, clave: string) => {
      const valor = ensureText(fila[clave] ?? "");
      const nombreCampo = articulosNombrePorColumna[clave];
      if (nombreCampo) {
        const descripcion = ensureText(fila[nombreCampo] ?? "");
        return descripcion ? `${valor} - ${descripcion}` : valor;
      }
      return valor;
    },
    [articulosNombrePorColumna],
  );

  const manejarExportarArticulosExcel = useCallback(() => {
    if (articulosCuenta.length === 0 || articulosColumnas.length === 0) {
      setArticulosExportError("No hay artículos para exportar.");
      return;
    }
    setArticulosExportError(null);
    setExportandoArticulosExcel(true);
    try {
      const filasExportacion = articulosCuenta.map((fila) => {
        const plano: Record<string, string> = {};
        articulosColumnas.forEach((clave) => {
          plano[humanizarColumnaArticulos(clave)] = construirTextoArticulo(fila, clave);
        });
        return plano;
      });
      const worksheet = XLSX.utils.json_to_sheet(filasExportacion);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "articulos");
      const base = cuentaSeleccionada?.codigo
        ? ensureText(cuentaSeleccionada.codigo).replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase()
        : "cuenta";
      const slug = base ? base : "cuenta";
      const timestamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `articulos-${slug}-${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos exportar los artículos a Excel.";
      setArticulosExportError(mensaje);
    } finally {
      setExportandoArticulosExcel(false);
    }
  }, [articulosCuenta, articulosColumnas, construirTextoArticulo, cuentaSeleccionada]);

  const manejarExportarArticulosPdf = useCallback(() => {
    if (articulosCuenta.length === 0 || articulosColumnas.length === 0) {
      setArticulosExportError("No hay artículos para exportar.");
      return;
    }
    setArticulosExportError(null);
    setExportandoArticulosPdf(true);
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let pageWidth = doc.internal.pageSize.getWidth();
      let pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 28;
      const marginY = 28;
      const lineHeight = 12;
      const headerHeight = 110;
      const columnWidth = Math.max((pageWidth - marginX * 2) / articulosColumnas.length, 60);
      const usuarioTexto = ensureText(resolverUsuarioActual());
      const timestamp = new Date();
      const timestampTexto = timestamp.toLocaleString("es-PY", {
        dateStyle: "short",
        timeStyle: "medium",
      });
      const tituloTexto = cuentaSeleccionada
        ? `Artículos vinculados a ${cuentaSeleccionada.codigo}`
        : "Artículos vinculados a cuenta";
      const descripcionTexto = cuentaSeleccionada?.nombre
        ? `Cuenta: ${cuentaSeleccionada.codigo} · ${cuentaSeleccionada.nombre}`
        : "Catálogo de artículos asociados a la cuenta seleccionada.";
      const servidorTexto = cuentaSeleccionada?.servidor
        ? `Servidor: ${formatServerLabel(cuentaSeleccionada.servidor)}`
        : "";
      let currentY = marginY;
      let pageNumber = 1;

      const renderFooter = () => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const footerText = `Generado por ${usuarioTexto} · ${timestampTexto} · Página ${pageNumber}`;
        doc.text(footerText, marginX, pageHeight - marginY / 2);
      };

      const renderHeader = (isFirstPage: boolean) => {
        if (!isFirstPage) {
          renderFooter();
          doc.addPage();
          pageNumber += 1;
          pageWidth = doc.internal.pageSize.getWidth();
          pageHeight = doc.internal.pageSize.getHeight();
        }
        currentY = marginY;
        if (logoDataUrl) {
          doc.addImage(logoDataUrl, "PNG", marginX, currentY - 4, 120, 26);
          currentY += 26;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text("Informes / Contabilidad / Artículos por cuenta", marginX, currentY + 6);
        currentY += 16;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text(tituloTexto, marginX, currentY + 6);
        currentY += 20;
        if (descripcionTexto) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(descripcionTexto, marginX, currentY + 4, {
            maxWidth: pageWidth - marginX * 2 - 150,
          });
          currentY += 18;
        }
        if (servidorTexto) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(servidorTexto, marginX, currentY + 2);
          currentY += 14;
        }
        doc.setFont("helvetica", "bold");
        doc.text("Usuario:", pageWidth - marginX, marginY + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(usuarioTexto || "—", pageWidth - marginX, marginY + 26, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Generado:", pageWidth - marginX, marginY + 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(timestampTexto || "—", pageWidth - marginX, marginY + 50, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`Página ${pageNumber}`, pageWidth - marginX, marginY + 62, { align: "right" });
        currentY = marginY + headerHeight;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        articulosColumnas.forEach((clave, index) => {
          const headerX = marginX + index * columnWidth;
          const texto = humanizarColumnaArticulos(clave);
          const lines = doc.splitTextToSize(texto, columnWidth - 6);
          lines.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, headerX, currentY + lineaIdx * lineHeight);
          });
        });
        currentY += lineHeight * 2;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
      };

      const renderRow = (row: Record<string, unknown>) => {
        const values = articulosColumnas.map((clave) =>
          construirTextoArticulo(row, clave),
        );
        const rowLines = values.map((texto) => doc.splitTextToSize(texto || "-", columnWidth - 6));
        const maxLines = Math.max(...rowLines.map((lineas) => (lineas.length > 0 ? lineas.length : 1)));
        const requiredHeight = maxLines * lineHeight + 6;
        if (currentY + requiredHeight > pageHeight - marginY) {
          renderHeader(false);
        }
        rowLines.forEach((lineas, index) => {
          const baseX = marginX + index * columnWidth;
          lineas.forEach((linea: string, lineaIdx: number) => {
            doc.text(linea, baseX, currentY + lineaIdx * lineHeight);
          });
        });
        currentY += requiredHeight;
      };

      renderHeader(true);
      articulosCuenta.forEach((fila) => renderRow(fila));
      renderFooter();
      const base = cuentaSeleccionada?.codigo
        ? ensureText(cuentaSeleccionada.codigo).replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase()
        : "cuenta";
      const slug = base ? base : "cuenta";
      const timestampArchivo = timestamp.toISOString().slice(0, 10);
      doc.save(`articulos-${slug}-${timestampArchivo}.pdf`);
    } catch (err) {
      console.error(err);
      const mensaje =
        err instanceof Error ? err.message : "No pudimos exportar los artículos a PDF.";
      setArticulosExportError(mensaje);
    } finally {
      setExportandoArticulosPdf(false);
    }
  }, [
    articulosCuenta,
    articulosColumnas,
    construirTextoArticulo,
    cuentaSeleccionada,
    logoDataUrl,
  ]);

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb
          title="Plan de cuentas contables"
          pageTitle="Contabilidad"
          description="Listado del plan contable general cruzado con tipos de cuenta y saldos."
        />
        <Card className="border-0 shadow-sm">
          <CardBody>
            {servidoresTabs.length > 1 ? (
              <Nav pills className="arrow-navtabs nav-success mb-3">
                {servidoresTabs.map((servidor) => {
                  const esActivo =
                    servidorActivo.toLowerCase() === servidor.toLowerCase();
                  const label = formatServerLabel(servidor);
                  return (
                    <NavItem key={servidor}>
                      <NavLink
                        href="#"
                        className={esActivo ? "active" : ""}
                        onClick={(event) => {
                          event.preventDefault();
                          setServidorActivo(servidor);
                        }}
                      >
                        {label}
                      </NavLink>
                    </NavItem>
                  );
                })}
              </Nav>
            ) : null}
            <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2 mb-3">
              <Input
                type="search"
                placeholder="Buscar cuenta o nombre..."
                value={filtroTexto}
                onChange={(event) => setFiltroTexto(event.target.value)}
                className="flex-grow-1"
              />
              <div className="d-flex flex-wrap justify-content-end gap-2 w-100 w-lg-auto">
              <Button color="primary" onClick={() => void cargarDatos()} disabled={cargando}>
                {cargando ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Actualizando
                  </>
                ) : (
                  <>
                    <i className="ri-refresh-line me-1" /> Actualizar
                  </>
                )}
              </Button>
              <Button
                color="success"
                onClick={manejarExportarExcel}
                disabled={registrosFiltrados.length === 0 || exportandoExcel}
              >
                {exportandoExcel ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Generando Excel
                  </>
                ) : (
                  <>
                    <i className="ri-file-excel-2-line me-1" /> Descargar Excel
                  </>
                )}
              </Button>
              <Button
                color="danger"
                onClick={manejarExportarPdf}
                disabled={registrosFiltrados.length === 0 || exportandoPdf}
              >
                {exportandoPdf ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Generando PDF
                  </>
                ) : (
                  <>
                    <i className="ri-file-pdf-line me-1" /> Descargar PDF
                  </>
                )}
              </Button>
              <Button color="primary" onClick={() => setFiltrosVisibles(true)}>
                <i className="ri-filter-3-line me-1" />
                Filtros
              </Button>
              </div>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}
            {exportError ? (
              <Alert color="warning" toggle={() => setExportError(null)}>
                {exportError}
              </Alert>
            ) : null}
            {cargando && datos.length === 0 ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : datos.length === 0 ? (
              <div className="text-center text-muted py-5">No encontramos registros para mostrar.</div>
            ) : registrosFiltrados.length === 0 ? (
              <div className="text-center text-muted py-5">
                No hay registros que coincidan con los filtros seleccionados.
              </div>
            ) : (
              <div style={tableWrapperStyle}>
                <DataTable
                  columns={dataTableColumns}
                  data={tableRows}
                  keyField="_rowId"
                  pagination
                  paginationPerPage={200}
                  paginationRowsPerPageOptions={[25, 50, 100, 200, 500]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Filas por página",
                    rangeSeparatorText: "de",
                    selectAllRowsItem: false,
                  }}
                  dense
                  highlightOnHover
                  pointerOnHover
                  striped
                  persistTableHead
                  theme={themeMode === "dark" ? "velzonDark" : "velzonLight"}
                  customStyles={dataTableStyles}
                  conditionalRowStyles={conditionalRowStyles as ConditionalStyles<Record<string, unknown>>[]}
                />
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
      <PlanCuentasFilters
        servidorActivo={servidorActivo}
        setServidorActivo={setServidorActivo}
        servidoresDisponibles={servidoresDisponibles}
        filtroNivel={filtroNivel}
        setFiltroNivel={setFiltroNivel}
        nivelesDisponibles={nivelesDisponibles}
        filtroSaldo={filtroSaldo}
        setFiltroSaldo={setFiltroSaldo}
        saldosDisponibles={saldosDisponibles}
        filtroTipoCuenta={filtroTipoCuenta}
        setFiltroTipoCuenta={setFiltroTipoCuenta}
        filtroTexto={filtroTexto}
        setFiltroTexto={setFiltroTexto}
        soloSinPadre={soloSinPadre}
        setSoloSinPadre={setSoloSinPadre}
        soloConAsiento={soloConAsiento}
        setSoloConAsiento={setSoloConAsiento}
        limpiarFiltros={limpiarFiltros}
        filtrosVisibles={filtrosVisibles}
        setFiltrosVisibles={setFiltrosVisibles}
      />
      <Offcanvas
        direction="end"
        scrollable
        isOpen={articulosVisibles}
        toggle={cerrarArticulos}
        style={FULLSCREEN_OFFCANVAS_STYLE}
      >
        <OffcanvasHeader toggle={cerrarArticulos}>
          {cuentaSeleccionada
            ? `Artículos vinculados a ${cuentaSeleccionada.codigo}`
            : "Artículos vinculados"}
        </OffcanvasHeader>
        <OffcanvasBody className="d-flex flex-column h-100">
          {cuentaSeleccionada ? (
            <div className="mb-3 text-muted">
              <div>
                <strong>Cuenta:</strong> {cuentaSeleccionada.codigo}
                {cuentaSeleccionada.nombre ? ` · ${cuentaSeleccionada.nombre}` : ""}
              </div>
              {cuentaSeleccionada.servidor ? (
                <div>
                  <strong>Servidor:</strong> {formatServerLabel(cuentaSeleccionada.servidor)}
                </div>
              ) : null}
            </div>
          ) : null}
          {articulosCargando ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-3 mb-0">Buscando artículos vinculados…</p>
            </div>
          ) : articulosError ? (
            <Alert color="danger">{articulosError}</Alert>
          ) : articulosCuenta.length === 0 ? (
            <div className="text-center text-muted py-5">
              No encontramos artículos vinculados a esta cuenta.
            </div>
          ) : articulosDataTableColumns.length === 0 ? (
            <div className="text-center text-muted py-5">
              No pudimos determinar las columnas para mostrar los artículos.
            </div>
          ) : (
            <div className="d-flex flex-column flex-grow-1 overflow-hidden">
              <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
                <Button
                  color="success"
                  onClick={manejarExportarArticulosExcel}
                  disabled={exportandoArticulosExcel || exportandoArticulosPdf}
                >
                  {exportandoArticulosExcel ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Generando Excel
                    </>
                  ) : (
                    <>
                      <i className="ri-file-excel-2-line me-1" /> Descargar Excel
                    </>
                  )}
                </Button>
                <Button
                  color="danger"
                  onClick={manejarExportarArticulosPdf}
                  disabled={exportandoArticulosPdf || exportandoArticulosExcel}
                >
                  {exportandoArticulosPdf ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Generando PDF
                    </>
                  ) : (
                    <>
                      <i className="ri-file-pdf-line me-1" /> Descargar PDF
                    </>
                  )}
                </Button>
              </div>
              {articulosExportError ? (
                <Alert color="warning" toggle={() => setArticulosExportError(null)}>
                  {articulosExportError}
                </Alert>
              ) : null}
              <div className="flex-grow-1 overflow-auto">
                <DataTable
                  columns={articulosDataTableColumns}
                  data={articulosTableRows}
                  keyField="_rowId"
                  pagination
                  paginationPerPage={50}
                  paginationRowsPerPageOptions={[25, 50, 100, 200]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Filas por página",
                    rangeSeparatorText: "de",
                    selectAllRowsItem: false,
                  }}
                  dense
                  highlightOnHover
                  pointerOnHover
                  striped
                  persistTableHead
                  theme={themeMode === "dark" ? "velzonDark" : "velzonLight"}
                  customStyles={dataTableStyles}
                  conditionalRowStyles={conditionalRowStyles as ConditionalStyles<Record<string, unknown>>[]}
                />
              </div>
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
    </div>
  );
};

type PlanCuentasFiltersProps = {
  servidorActivo: string;
  setServidorActivo: (valor: string) => void;
  servidoresDisponibles: string[];
  filtroNivel: string;
  setFiltroNivel: (valor: string) => void;
  nivelesDisponibles: string[];
  filtroSaldo: string;
  setFiltroSaldo: (valor: string) => void;
  saldosDisponibles: string[];
  filtroTipoCuenta: string;
  setFiltroTipoCuenta: (valor: string) => void;
  filtroTexto: string;
  setFiltroTexto: (valor: string) => void;
  soloSinPadre: boolean;
  setSoloSinPadre: (valor: boolean) => void;
  soloConAsiento: boolean;
  setSoloConAsiento: (valor: boolean) => void;
  limpiarFiltros: () => void;
  filtrosVisibles: boolean;
  setFiltrosVisibles: (valor: boolean) => void;
};

const PlanCuentasFilters: React.FC<PlanCuentasFiltersProps> = ({
  servidorActivo,
  setServidorActivo,
  servidoresDisponibles,
  filtroNivel,
  setFiltroNivel,
  nivelesDisponibles,
  filtroSaldo,
  setFiltroSaldo,
  saldosDisponibles,
  filtroTipoCuenta,
  setFiltroTipoCuenta,
  filtroTexto,
  setFiltroTexto,
  soloSinPadre,
  setSoloSinPadre,
  soloConAsiento,
  setSoloConAsiento,
  limpiarFiltros,
  filtrosVisibles,
  setFiltrosVisibles,
}) => {
  return (
    <Offcanvas direction="end" scrollable isOpen={filtrosVisibles} toggle={() => setFiltrosVisibles(false)}>
      <OffcanvasHeader toggle={() => setFiltrosVisibles(false)}>Filtros del plan de cuentas</OffcanvasHeader>
      <OffcanvasBody>
        <FormGroup>
          <Label for="filtro-servidor">Servidor</Label>
          <Input
            id="filtro-servidor"
            type="select"
            value={servidorActivo}
            onChange={(event) => setServidorActivo(event.target.value)}
          >
            {servidoresDisponibles.map((servidor) => (
              <option key={servidor} value={servidor}>
                {formatServerLabel(servidor)}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label for="filtro-nivel">Nivel de cuenta</Label>
          <Input id="filtro-nivel" type="select" value={filtroNivel} onChange={(event) => setFiltroNivel(event.target.value)}>
            <option value="">Todos</option>
            {nivelesDisponibles.map((nivel) => (
              <option key={nivel} value={nivel}>
                Nivel {nivel}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label for="filtro-saldo">Tipo de saldo</Label>
          <Input id="filtro-saldo" type="select" value={filtroSaldo} onChange={(event) => setFiltroSaldo(event.target.value)}>
            <option value="">Todos</option>
            {saldosDisponibles.map((saldo) => (
              <option key={saldo} value={saldo.toLowerCase()}>
                {saldo}
              </option>
            ))}
          </Input>
        </FormGroup>
        <FormGroup>
          <Label for="filtro-tipo-cuenta">Tipo de cuenta</Label>
          <Input
            id="filtro-tipo-cuenta"
            value={filtroTipoCuenta}
            onChange={(event) => setFiltroTipoCuenta(event.target.value)}
            placeholder="Buscar por nombre o código"
          />
        </FormGroup>
        <FormGroup>
          <Label for="filtro-texto">Buscar por cuenta</Label>
          <Input
            id="filtro-texto"
            value={filtroTexto}
            onChange={(event) => setFiltroTexto(event.target.value)}
            placeholder="Buscar por código o descripción"
          />
        </FormGroup>
        <div className="form-check mb-2">
          <Input
            id="solo-sin-padre"
            type="checkbox"
            className="form-check-input"
            checked={soloSinPadre}
            onChange={(event) => setSoloSinPadre(event.target.checked)}
          />
          <Label className="form-check-label" htmlFor="solo-sin-padre">
            Mostrar sólo cuentas raíz
          </Label>
        </div>
        <div className="form-check mb-3">
          <Input
            id="solo-con-asiento"
            type="checkbox"
            className="form-check-input"
            checked={soloConAsiento}
            onChange={(event) => setSoloConAsiento(event.target.checked)}
          />
          <Label className="form-check-label" htmlFor="solo-con-asiento">
            Mostrar sólo cuentas con asiento
          </Label>
        </div>
        <div className="d-flex gap-2 mt-3">
          <Button color="primary" onClick={() => setFiltrosVisibles(false)}>
            Aplicar filtros
          </Button>
          <Button color="light" type="button" onClick={limpiarFiltros}>
            Limpiar
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default ContabilidadPlanCuentasPage;
